import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Edit } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { ProspectForm } from './ProspectForm';
import { prospectSchema, type ProspectFormData } from '@/lib/validations/prospect';
import { useProspects, type Prospect } from '@/hooks/useProspects';

interface EditProspectDialogProps {
  prospect: Prospect;
  trigger?: React.ReactNode;
}

export function EditProspectDialog({ prospect, trigger }: EditProspectDialogProps) {
  const [open, setOpen] = useState(false);
  const { editProspect } = useProspects();

  const form = useForm<ProspectFormData>({
    resolver: zodResolver(prospectSchema),
    defaultValues: {
      company: prospect.company,
      contactName: prospect.contactName,
      email: prospect.email,
      phone: prospect.phone || '',
      stage: prospect.stage,
      notes: prospect.notes || '',
    },
  });

  // Reset form when prospect changes or dialog opens
  useEffect(() => {
    if (open) {
      form.reset({
        company: prospect.company,
        contactName: prospect.contactName,
        email: prospect.email,
        phone: prospect.phone || '',
        stage: prospect.stage,
        notes: prospect.notes || '',
      });
    }
  }, [open, prospect, form]);

  const onSubmit = async (data: ProspectFormData) => {
    try {
      await editProspect(prospect._id, data);
      toast.success('Prospect updated successfully');
      setOpen(false);
    } catch (error) {
      console.error('Failed to update prospect:', error);
      toast.error('Failed to update prospect. Please try again.');
    }
  };

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
    if (!newOpen) {
      // Reset to original values when closing
      form.reset({
        company: prospect.company,
        contactName: prospect.contactName,
        email: prospect.email,
        phone: prospect.phone || '',
        stage: prospect.stage,
        notes: prospect.notes || '',
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        {trigger || (
          <Button variant="outline" size="sm">
            <Edit className="h-4 w-4" />
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Edit Prospect</DialogTitle>
          <DialogDescription>
            Update the details for {prospect.company}.
          </DialogDescription>
        </DialogHeader>
        
        <ProspectForm form={form} onSubmit={onSubmit}>
          <div className="flex justify-end gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => handleOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={form.formState.isSubmitting}>
              {form.formState.isSubmitting ? 'Updating...' : 'Update Prospect'}
            </Button>
          </div>
        </ProspectForm>
      </DialogContent>
    </Dialog>
  );
}